import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Server, ExternalLink, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ExternalSystemEditDialog, { type ExternalSystemOption } from './ExternalSystemEditDialog';

interface ExternalSystemInfoProps {
  externalSystem: string | null;
  externalSystemRecordId: string | null;
  externalSystemId?: string | null;
  availableSystems?: ExternalSystemOption[];
  onSave?: (system: string | null, recordId: string | null) => Promise<void>;
}

export default function ExternalSystemInfo({
  externalSystem,
  externalSystemRecordId,
  externalSystemId,
  availableSystems = [],
  onSave,
}: ExternalSystemInfoProps) {
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const isEditable = !!onSave;

  return (
    <>
      <div className="flex items-start gap-3 p-3 bg-secondary/50 rounded-lg relative">
        <div className="w-8 h-8 rounded-md bg-muted flex items-center justify-center flex-shrink-0">
          <Server className="w-4 h-4 text-muted-foreground" />
        </div>
        <div className="min-w-0 space-y-2 flex-1">
          {/* External System */}
          <div>
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              External System
            </p>
            {externalSystem ? (
              externalSystemId ? (
                <Link
                  to={`/configuration/external-systems/${externalSystemId}`}
                  className="text-sm text-primary hover:underline inline-flex items-center gap-1"
                >
                  {externalSystem}
                  <ExternalLink className="w-3 h-3" />
                </Link>
              ) : (
                <p className="text-sm text-foreground">{externalSystem}</p>
              )
            ) : (
              <p className="text-sm text-muted-foreground">(no system link)</p>
            )}
          </div>

          {/* External Record ID */}
          <div>
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              External Record ID
            </p>
            {externalSystemRecordId ? (
              <p className="text-sm text-foreground font-mono">{externalSystemRecordId}</p>
            ) : (
              <p className="text-sm text-muted-foreground font-mono">(no record id)</p>
            )}
          </div>
        </div>

        {/* Edit Icon */}
        {isEditable && (
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-2 right-2 h-7 w-7"
            onClick={() => setEditDialogOpen(true)}
          >
            <Pencil className="w-3.5 h-3.5" />
          </Button>
        )}
      </div>

      {/* Edit Dialog */}
      {isEditable && (
        <ExternalSystemEditDialog
          open={editDialogOpen}
          onOpenChange={setEditDialogOpen}
          externalSystem={externalSystem}
          externalSystemRecordId={externalSystemRecordId}
          availableSystems={availableSystems}
          onSave={onSave}
        />
      )}
    </>
  );
}
